import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Button, Avatar, Collapse,
} from '@mui/material';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../contexts/AuthContext.jsx';
import api from '../services/api.js';
import useNextDesign from '../next/useNextDesign';
import NextAdminShell from '../next/NextAdminShell';
import NextDesignPromo from '../next/NextDesignPromo';
import { NAV_ITEMS, SYSTEM_LABEL, visibleNavItems } from '../next/adminNav';

const DRAWER_WIDTH = 260;

/** old-design icons per menu id (the menu itself comes from next/adminNav — one source for both designs) */
const LEGACY_ICONS = {
  dashboard: HomeOutlinedIcon,
  calendar: CalendarMonthOutlinedIcon,
  members: PeopleOutlinedIcon,
  exams: AssignmentOutlinedIcon,
  'american-exams': QuizOutlinedIcon,
  examinees: AssignmentTurnedInOutlinedIcon,
  kolels: MapsHomeWorkOutlinedIcon,
  registrations: HowToRegOutlinedIcon,
  'exam-periods': EventNoteOutlinedIcon,
  'exam-cities': LocationCityOutlinedIcon,
  'pending-registrations': HowToRegOutlinedIcon,
  'gabbai-orders': Inventory2OutlinedIcon,
  'gemara-orders': MenuBookOutlinedIcon,
  'exam-library': MenuBookOutlinedIcon,
  labels: QrCodeOutlinedIcon,
  content: CampaignOutlinedIcon,
  'narration-catalog': RecordVoiceOverOutlinedIcon,
  ivr: PhoneInTalkOutlinedIcon,
  'ivr-templates': PhoneInTalkOutlinedIcon,
  'email-automations': MailOutlineIcon,
  subscriptions: NotificationsOutlinedIcon,
  reports: BarChartOutlinedIcon,
  'audit-log': FactCheckOutlinedIcon,
  'feature-flags': FlagOutlinedIcon,
  'email-domain': MailOutlineIcon,
  roles: AdminPanelSettingsOutlinedIcon,
  users: ManageAccountsOutlinedIcon,
};

const navItemSx = (isActive) => ({
  borderRadius: 2,
  mb: 0.5,
  py: 1,
  color: isActive ? '#fff' : 'rgba(255,255,255,0.75)',
  bgcolor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.1)',
    color: '#fff',
  },
  '&.Mui-selected': {
    bgcolor: 'rgba(255,255,255,0.18)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
  },
});

const Layout = () => {
  const nextDesign = useNextDesign();
  if (nextDesign.enabled) {
    return (
      <NextAdminShell>
        <Outlet />
      </NextAdminShell>
    );
  }
  return <LegacyLayout />;
};

const LegacyLayout = () => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activePeriod, setActivePeriod] = useState(undefined); // undefined=טוען, null=אין
  const [openMenus, setOpenMenus] = useState(() => {
    const initial = {};
    NAV_ITEMS.forEach((item) => {
      if (item.children?.length && location.pathname.startsWith(`${item.path}/`)) {
        initial[item.path] = true;
      }
    });
    return initial;
  });

  useEffect(() => {
    api.get('/exam-periods/active')
      .then(({ data }) => setActivePeriod(data || null))
      .catch(() => setActivePeriod(null));
  }, []);

  useEffect(() => {
    NAV_ITEMS.forEach((item) => {
      if (item.children?.length && location.pathname.startsWith(`${item.path}/`)) {
        setOpenMenus((prev) => (prev[item.path] ? prev : { ...prev, [item.path]: true }));
      }
    });
  }, [location.pathname]);

  // שם המערכת מגיע ממשתנה סביבה — כל deployment מגדיר את שלו
  const systemLabel = SYSTEM_LABEL;

  const toggleMenu = (path) => {
    setOpenMenus((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f4f8' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            right: 0,
            left: 'auto',
            background: 'linear-gradient(180deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)',
            color: '#fff',
            borderLeft: 'none',
          },
        }}
        anchor="right"
      >
        {/* Logo / Brand */}
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: 0.5 }}
          >
            {systemLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            ממשק ניהול
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

        {/* Navigation */}
        <List sx={{ px: 1.5, pt: 1.5, flex: 1 }}>
          {visibleNavItems(user, hasPermission).map((item) => {
            const Icon = LEGACY_ICONS[item.id] || HomeOutlinedIcon;
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const isChildActive = hasChildren
              && item.children.some((c) => (
                location.pathname === c.path
                || location.pathname.startsWith(`${c.path}/`)
              ));
            const isActive = hasChildren
              ? isChildActive || location.pathname.startsWith(`${item.path}/`)
              : location.pathname === item.path
                || location.pathname.startsWith(`${item.path}/`);
            const isOpen = !!openMenus[item.path];

            if (hasChildren) {
              return (
                <Box key={item.path}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => toggleMenu(item.path)}
                    sx={navItemSx(isActive)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 500,
                      }}
                    />
                    {isOpen
                      ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }} />
                      : <ExpandMore sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }} />}
                  </ListItemButton>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        const childActive = location.pathname === child.path
                          || location.pathname.startsWith(`${child.path}/`);
                        return (
                          <ListItemButton
                            key={child.path}
                            selected={childActive}
                            onClick={() => navigate(child.path)}
                            sx={{
                              ...navItemSx(childActive),
                              pr: 2,
                              pl: 2,
                              py: 0.75,
                              mr: 0.5,
                              ml: 1.5,
                            }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: childActive ? 700 : 500,
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            return (
              <ListItemButton
                key={item.path}
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={navItemSx(isActive)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                  }}
                >
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isActive ? 700 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>

        {/* User info at bottom */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 34, height: 34,
              bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: 14, fontWeight: 700,
            }}
          >
            {(user?.username || 'א').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#fff' }}>
              {user?.username || 'אורח'}
            </Typography>
          </Box>
          <Button
            onClick={logout}
            size="small"
            sx={{
              minWidth: 'auto', p: 0.8,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <LogoutIcon fontSize="small" />
          </Button>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ minHeight: '100vh', p: 3, pt: 2 }}>
          <Outlet />
        </Box>
      </Box>
      {/* old design: invite to switch back to the new design (push card, bottom-right) */}
      <NextDesignPromo />
    </Box>
  );
};

export default Layout;
